/**
 * A start-format card that shows how the format feels: a still of its most telling moment, and on hover (or keyboard
 * focus) the short sample playing, muted and looping — drawn by the same scene library as the real video.
 */
import { useMemo, useState } from 'react';
import { Player, Thumbnail } from '@remotion/player';
import { VideoComposition } from '@/video/VideoComposition';
import { ASPECT_SIZE, VIDEO_FPS, totalFrames } from '@/video/spec';
import { STILL_AT, starterPreview } from './starterPreviews';
import type { VideoStarter } from './videoStudioShared';

export function StarterCard({ starter, selected, onPick }: { starter: VideoStarter; selected: boolean; onPick: () => void }) {
  const [live, setLive] = useState(false);
  const spec = useMemo(() => starterPreview(starter.id), [starter.id]);
  const input = useMemo(() => ({ spec }), [spec]);
  const size = ASPECT_SIZE[spec.aspect];
  const frames = Math.max(1, totalFrames(spec));
  const still = Math.min(frames - 1, Math.round((STILL_AT[starter.id] ?? 0.6) * VIDEO_FPS));
  const box = 'pointer-events-none h-full w-full';
  return (
    <button type="button" onClick={onPick} aria-pressed={selected}
      onMouseEnter={() => setLive(true)} onMouseLeave={() => setLive(false)} onFocus={() => setLive(true)} onBlur={() => setLive(false)}
      className={`group flex gap-3 rounded-xl border bg-white p-2.5 text-left transition-colors ${selected ? 'border-[#be43ff] ring-1 ring-[#be43ff]' : 'border-[#eaecf0] hover:border-[#d0d5dd]'}`}>
      <span className="relative h-[152px] w-[86px] shrink-0 overflow-hidden rounded-lg bg-[#2e024c] shadow-[0_2px_8px_rgba(16,24,40,0.12)]">
        {live ? (
          <Player component={VideoComposition} inputProps={input} durationInFrames={frames} compositionWidth={size.width} compositionHeight={size.height} fps={VIDEO_FPS}
            autoPlay loop initiallyMuted numberOfSharedAudioTags={0} controls={false} clickToPlay={false} className={box} style={{ width: '100%', height: '100%' }} />
        ) : (
          <Thumbnail component={VideoComposition} inputProps={input} durationInFrames={frames} compositionWidth={size.width} compositionHeight={size.height} fps={VIDEO_FPS} frameToDisplay={still} className={box} style={{ width: '100%', height: '100%' }} />
        )}
        {!live && <span className="absolute bottom-1 right-1 rounded bg-black/55 px-1 text-[9px] font-semibold text-white opacity-80 group-hover:opacity-0">▶ {Math.round(frames / VIDEO_FPS)}s</span>}
      </span>
      <span className="flex min-w-0 flex-col gap-1 py-0.5">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#101828]"><starter.icon className="h-4 w-4 shrink-0 text-[#7a1fbf]" />{starter.name}</span>
        <span className="text-[12px] leading-snug text-[#667085]">{starter.line}</span>
      </span>
    </button>
  );
}
